export const GtmService = {
    /**
     * Pushes an event to the dataLayer.
     * @param {string} event
     * @param {object} data
     */
    pushToDataLayer: (event, data) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event,
            ...data
        });
    },

    /**
     * Empuja un evento de ecommerce con el objeto `ecommerce` que es de donde leen las
     * etiquetas GA4 de GTM ("Incluir datos de ecommerce" / dataLayer). Antes los items
     * iban al nivel raiz y GA4 los ignoraba. El push previo a null limpia el objeto
     * anterior (recomendacion de Google para que no se arrastren items entre eventos).
     * @param {string} event
     * @param {object} ecommerce
     * @param {object} [extra] - Claves adicionales al nivel raiz (para params sueltos)
     */
    pushEcommerce: (event, ecommerce, extra = {}) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event, ecommerce, ...extra });
    },

    /**
     * Logs a view_item event.
     * @param {object} product - The product details
     */
    logViewItem: (product) => {
        if (!product) return;

        GtmService.pushEcommerce('view_item', {
            items: [{
                item_id: product.id || product.code,
                item_name: product.name,
                item_category: product.category || product.manufacturer, // Fallback if category is missing
                price: product.price?.sellingPrice || product.price?.value || 0,
                quantity: 1
            }]
        });
    },

    /**
     * Logs an add_to_cart event.
     * @param {object} product - The product details
     * @param {number} quantity - The quantity added
     * @param {object} price - The price details applied
     */
    logAddToCart: (product, quantity, price) => {
        if (!product) return;

        GtmService.pushEcommerce('add_to_cart', {
            items: [{
                item_id: product.code || product.id,
                item_name: product.name,
                item_category: product.category || product.manufacturer,
                price: price?.sellingPrice || product.price?.sellingPrice || 0,
                quantity: quantity
            }]
        });
    },

    /**
     * Logs a begin_checkout event.
     * @param {object} cart - The current cart state
     */
    logBeginCheckout: (cart) => {
        if (!cart || !cart.products) return;

        const items = cart.products.map(item => ({
            item_id: item.code || item.id,
            item_name: item.name,
            item_category: item.category || item.manufacturer,
            price: item.sellingPrice || item.price?.sellingPrice || 0,
            quantity: item.quantity
        }));

        GtmService.pushEcommerce('begin_checkout', { items });
    },

    /**
     * Identifica al usuario logueado: GA4 user_id + propiedades para segmentar TODO lo
     * demas (cliente vs comercial, y por que cliente esta actuando el comercial).
     * Se re-emite cuando el comercial cambia de cliente suplantado.
     * @param {{code: string, isComercial: boolean}} user
     * @param {{numCliente: string}|null} actingAsCliente
     */
    logUserData: (user, actingAsCliente) => {
        if (!user?.code) return;

        // La etiqueta de Google de GTM lee el user_id de localStorage al cargar la pagina
        // (variable "js - stored_user_id"): la identidad llega ANTES que esta llamada
        // asincrona en las cargas siguientes.
        try {
            localStorage.setItem('lv_analytics_user', JSON.stringify({ user_id: String(user.code) }));
        } catch { /* almacenamiento bloqueado: la analitica sigue sin identidad */ }

        GtmService.pushToDataLayer('user_data', {
            user_id: String(user.code),
            user_type: user.isComercial ? 'comercial' : 'cliente',
            acting_as_client: actingAsCliente?.numCliente ? String(actingAsCliente.numCliente) : null,
        });
    },

    /**
     * Logs a search event (GA4 estandar: search / search_term).
     * @param {string} searchTerm
     */
    logSearch: (searchTerm) => {
        if (!searchTerm) return;
        GtmService.pushToDataLayer('search', { search_term: searchTerm });
    },

    /**
     * Clic en un producto desde un listado, con su origen (catalogo, ofertas, nuevos,
     * sugerencias...) para saber DESDE DONDE se llega a las fichas.
     * @param {{code?: string, id?: string, name?: string, nombre?: string}} product
     * @param {string} listName - Origen del clic (p. ej. pathname o 'sugerencias')
     */
    logSelectItem: (product, listName) => {
        if (!product) return;

        GtmService.pushEcommerce('select_item', {
            item_list_name: listName,
            items: [{
                item_id: product.code || product.id,
                item_name: product.name || product.nombre,
            }]
        }, { item_list_name: listName });
    },

    /**
     * Cambio en los filtros, con el estado resultante legible (etiquetas de los chips).
     * @param {string} filterType - Que se ha tocado (taxonomyTree, manufacturerIds, quitar_chip...)
     * @param {string} filterValue - Estado legible resultante ("Medicamentos | BAYER")
     * @param {string} section - Donde (pathname: /articulos, /promociones, /nuevos)
     */
    logFilterApply: (filterType, filterValue, section) => {
        GtmService.pushToDataLayer('filter_apply', {
            filter_type: filterType,
            filter_value: filterValue,
            section: section,
        });
    },

    /**
     * Vista de una seccion especial (Ofertas/Nuevos) y su pestana activa.
     * @param {string} sectionName
     * @param {string} [tab]
     */
    logSectionView: (sectionName, tab) => {
        GtmService.pushToDataLayer('section_view', {
            section_name: sectionName,
            section_tab: tab || null,
        });
    },

    /**
     * Clic en la navegacion superior (categorias del menu, pildoras Nuevos/Ofertas).
     * @param {string} navItem - Etiqueta legible ("Medicamentos > Antiparasitarios")
     */
    logNavClick: (navItem) => {
        GtmService.pushToDataLayer('nav_click', { nav_item: navItem });
    },

    /**
     * Logs a remove_from_cart event (GA4 estandar).
     * @param {object} product - Linea del carrito
     * @param {number} quantity
     */
    logRemoveFromCart: (product, quantity) => {
        if (!product) return;

        GtmService.pushEcommerce('remove_from_cart', {
            items: [{
                item_id: product.code || product.id,
                item_name: product.name || product.nombre,
                price: product.sellingPrice || product.price?.sellingPrice || 0,
                quantity: quantity ?? product.quantity ?? 1,
            }]
        });
    },

    /**
     * Logs a purchase event.
     * @param {string} transactionId
     * @param {number} total
     * @param {number} tax
     * @param {object[]} cartItems
     */
    logPurchase: (transactionId, total, tax, cartItems) => {
        const items = cartItems.map(item => ({
            item_id: item.code || item.id,
            item_name: item.name,
            item_category: item.category || item.manufacturer,
            price: item.sellingPrice || item.price?.sellingPrice || 0,
            quantity: item.quantity
        }));

        GtmService.pushEcommerce('purchase', {
            transaction_id: transactionId,
            value: total,
            currency: 'EUR',
            tax: tax,
            shipping: 0, // Assuming 0 for now as per req
            items: items
        });
    }
};
